import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Stack,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Palette as PaletteIcon,
  Check as CheckIcon,
  LightMode as SunIcon,
  DarkMode as MoonIcon,
  AutoAwesome as SparkleIcon,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import { ThemeDataContext } from "../../context/ThemeContext";

const DRAWER_W = 300;

// ── Mini color swatch preview ─────────────────────────────────────────────────
const ThemePreview = ({ themeData, mode }) => {
  const d = themeData || {};
  const isDark = mode === "dark";

  const bg = isDark
    ? d.background_default_dark || "#111111"
    : d.background_default_light || "#ffffff";

  const paper = isDark
    ? d.background_paper_dark || "#1e1e1e"
    : d.background_paper_light || "#f5f5f5";

  const primary = isDark
    ? d.primary_dark || "#ffffff"
    : d.primary_light || "#000000";

  const text = isDark
    ? d.text_primary_dark || "#eeeeee"
    : d.text_primary_light || "#111111";

  const divider = isDark
    ? d.divider_dark || "rgba(255,255,255,0.1)"
    : d.divider_light || "rgba(0,0,0,0.1)";

  return (
    <Box
      sx={{
        width: "100%",
        height: 80,
        borderRadius: "10px",
        overflow: "hidden",
        border: `1.5px solid ${divider}`,
        bgcolor: bg,
        p: "8px",
        display: "flex",
        flexDirection: "column",
        gap: "5px",
        flexShrink: 0,
      }}
    >
      {/* Topbar */}
      <Box
        sx={{
          bgcolor: paper,
          borderRadius: "5px",
          height: 16,
          display: "flex",
          alignItems: "center",
          px: "6px",
          gap: "4px",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: primary,
            flexShrink: 0,
          }}
        />
        <Box
          sx={{
            flex: 1,
            height: 3,
            borderRadius: 2,
            bgcolor: `${text}22`,
          }}
        />
        <Box
          sx={{
            width: 14,
            height: 6,
            borderRadius: "3px",
            bgcolor: primary,
            flexShrink: 0,
          }}
        />
      </Box>

      {/* Body */}
      <Box sx={{ display: "flex", gap: "5px", flex: 1, minHeight: 0 }}>
        {/* Sidebar strip */}
        <Box
          sx={{
            width: 26,
            bgcolor: paper,
            borderRadius: "5px",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: "3px",
            p: "4px",
          }}
        >
          {[1, 0.4, 0.4, 0.4].map((op, i) => (
            <Box
              key={i}
              sx={{
                height: 3,
                borderRadius: 2,
                bgcolor:
                  i === 0
                    ? primary
                    : `${text}${Math.round(op * 255)
                        .toString(16)
                        .padStart(2, "0")}`,
              }}
            />
          ))}
        </Box>

        {/* Content area */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <Box
            sx={{
              height: 5,
              borderRadius: 2,
              bgcolor: `${text}33`,
              width: "80%",
            }}
          />
          <Box
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: `${text}1a`,
              width: "60%",
            }}
          />
          <Box sx={{ flex: 1 }} />
          <Box
            sx={{
              height: 8,
              borderRadius: "4px",
              bgcolor: primary,
              width: "45%",
              alignSelf: "flex-start",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

// ── Single theme card ─────────────────────────────────────────────────────────
const ThemeCard = ({ meta, isActive, onSelect, mode, lang }) => {
  const theme = useTheme();

  return (
    <Box
      onClick={() => onSelect(meta)}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: `1.5px solid ${
          isActive ? theme.palette.primary.main : theme.palette.divider
        }`,
        bgcolor: isActive
          ? alpha(theme.palette.primary.main, 0.06)
          : "background.paper",
        cursor: "pointer",
        transition: "all 0.15s ease",
        position: "relative",
        "&:hover": {
          borderColor: theme.palette.primary.main,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          transform: "translateY(-1px)",
        },
      }}
    >
      {/* Active checkmark */}
      {isActive && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 20,
            height: 20,
            borderRadius: "50%",
            bgcolor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`,
          }}
        >
          <CheckIcon sx={{ fontSize: 12, color: "primary.contrastText" }} />
        </Box>
      )}

      {/* Preview */}
      <ThemePreview themeData={meta.themeData} mode={mode} />

      {/* Name + desc */}
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="body2"
          fontWeight={isActive ? 700 : 600}
          color={isActive ? "primary.main" : "text.primary"}
          sx={{ fontSize: "0.8rem", lineHeight: 1.3 }}
          noWrap
        >
          {meta.name}
        </Typography>
        {meta.description && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ fontSize: "0.68rem", display: "block", mt: 0.25 }}
            noWrap
          >
            {meta.description}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ── Main Drawer ───────────────────────────────────────────────────────────────
const ThemeDrawer = ({ open, onClose }) => {
  const theme = useTheme();
  const { hitAxios } = useContext(GlobalContext);
  const { lang } = useContext(TranslateContext);
  const { mode, toggleColorMode, setThemeConfig } =
    useContext(ThemeDataContext);

  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState(
    () => localStorage.getItem("user_theme_id") || null,
  );

  // ── Load all themes with data in one shot ──
  useEffect(() => {
    if (!open || themes.length > 0) return;
    loadThemes();
  }, [open]);

  async function loadThemes() {
    setLoading(true);
    const res = await hitAxios({
      path: "/api/theme/list-all-themes-with-data",
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    setLoading(false);
    if (res?.data?.success) {
      setThemes(res.data.data || []);
    }
  }

  // ── Select a theme ──
  function handleSelectTheme(meta) {
    setActiveId(meta.id);
    localStorage.setItem("user_theme_id", meta.id);
    if (meta.themeData) {
      localStorage.setItem("user_theme_data", JSON.stringify(meta.themeData));
      if (setThemeConfig) {
        setThemeConfig(meta.themeData);
      }
    }
  }

  // ── Toggle dark/light ──
  function handleToggleMode(value) {
    if (mode !== value) {
      toggleColorMode(window.innerWidth / 2, window.innerHeight / 2);
    }
  }

  const isDark = mode === "dark";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: DRAWER_W,
          bgcolor: "background.default",
          border: "none",
          borderLeft: `1px solid ${theme.palette.divider}`,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "8px",
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "primary.main",
            flexShrink: 0,
          }}
        >
          <PaletteIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={700}
            color="text.primary"
            sx={{ lineHeight: 1.3 }}
          >
            {lang?.appearance || "Appearance"}
          </Typography>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ fontSize: "0.68rem" }}
          >
            {lang?.customizeTheme || "Customize your theme"}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: "text.secondary", flexShrink: 0 }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* ── Scrollable body ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2,
          py: 2,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: alpha(theme.palette.text.primary, 0.12),
            borderRadius: 2,
          },
        }}
      >
        {/* ── Color mode ── */}
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.68rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "text.disabled",
            mb: 1,
            display: "block",
          }}
        >
          {lang?.colorMode || "Color Mode"}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
          {[
            {
              value: "light",
              icon: SunIcon,
              label: lang?.lightMode || "Light",
            },
            {
              value: "dark",
              icon: MoonIcon,
              label: lang?.darkMode || "Dark",
            },
          ].map(({ value, icon: Icon, label }) => {
            const isSelected = mode === value;
            return (
              <Box
                key={value}
                onClick={() => handleToggleMode(value)}
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.75,
                  py: 1.5,
                  borderRadius: 2,
                  border: `1.5px solid ${
                    isSelected
                      ? theme.palette.primary.main
                      : theme.palette.divider
                  }`,
                  bgcolor: isSelected
                    ? alpha(theme.palette.primary.main, 0.07)
                    : "background.paper",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                <Icon
                  sx={{
                    fontSize: 20,
                    color: isSelected ? "primary.main" : "text.disabled",
                  }}
                />
                <Typography
                  variant="caption"
                  fontWeight={isSelected ? 700 : 400}
                  color={isSelected ? "primary.main" : "text.secondary"}
                  sx={{ fontSize: "0.72rem" }}
                >
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* ── Themes list ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.68rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "text.disabled",
            }}
          >
            {lang?.themes || "Themes"}
          </Typography>
          {themes.length > 0 && (
            <Chip
              label={themes.length}
              size="small"
              sx={{
                height: 16,
                fontSize: "0.6rem",
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={22} sx={{ color: "primary.main" }} />
          </Box>
        ) : themes.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <SparkleIcon sx={{ fontSize: 28, color: "text.disabled", mb: 1 }} />
            <Typography
              variant="caption"
              color="text.disabled"
              display="block"
              sx={{ fontSize: "0.75rem" }}
            >
              {lang?.noThemesFound || "No themes found"}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {themes.map((meta) => (
              <ThemeCard
                key={meta.id}
                meta={meta}
                isActive={activeId === meta.id}
                onSelect={handleSelectTheme}
                mode={mode}
                lang={lang}
              />
            ))}
          </Stack>
        )}
      </Box>

      {/* ── Footer ── */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper",
          flexShrink: 0,
        }}
      >
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{
            fontSize: "0.65rem",
            display: "block",
            textAlign: "center",
            lineHeight: 1.5,
          }}
        >
          {lang?.themePreferenceNote || "Saved locally on this device only"}
        </Typography>
      </Box>
    </Drawer>
  );
};

export default ThemeDrawer;
